import React from "react";

const HowToPitchVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
        This lesson treats the promotion ask as a high stakes anchor conversation, not a casual chat. It shows why people freeze when asked why they deserve it, and how preparation changes the outcome. It covers common pushbacks like delay tactics, vague soft skills feedback, tenure comparisons, budget excuses, being too essential, moving goalposts, and threatened managers. The core move is to arrive with proof, a clear differentiator, and a next level scope story that is already true.
      </p>
    </div>
  );

const transcript = (
  <div className="pt-2 pb-6 space-y-4 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>
      It’s Monday morning. Sara’s at the office, bright, confident, armed with{" "}
      <em>caffeine</em> and <em>courage</em>. Today’s the day. The day she asks
      for that promotion.
    </p>

    <p>
      She sits down, looks her boss in the eye and says,{" "}
      <em>“I’d like to discuss my growth here.”</em>
      <br />
      <strong>Boss</strong> smiles and says,{" "}
      <em>
        “Sure, Sara! But why do you think you deserve a promotion?”
      </em>
    </p>

    <p>
      And blank! Her mind’s as empty as that Google Doc you swore you’d{" "}
      “finish later.”
    </p>

    <p>
      We’ve all been there. You either get defensive,{" "}
      <em>big mistake</em>. Or you stutter,{" "}
      <em>bigger mistake</em>. Both scream one thing:{" "}
      <em>unprepared</em>.
    </p>

    <p>
      This conversation is not just another chat.
      <br />
      It’s your <em>anchor meeting</em>. The one conversation that can change
      your trajectory.
      <br />
      So, if you just felt motivated to get up and storm into your boss’s
      office….
      <br />
      <strong>Sit down!</strong>
    </p>

    <p>
      Let’s play through every way this can go wrong, and how{" "}
      <em>you can flip the script.</em>
    </p>

    <p className="text-[#B00020] font-normal">
      The “Let’s review it later” classic. <span className="text-[#323232]">They’re not convinced it’ll cost
      them anything to delay.</span>
    </p>

    <p>
      This is where you can trigger their loss aversion. But we talked about
      how people act faster when they fear losing something. So before you
      trigger loss aversion, make sure you’re{" "}
      <em>worth losing.</em>
    </p>

    <p>
      Be the person whose absence creates a dent, in results, in continuity,
      maybe even in their peace of mind.
    </p>

    <p className="text-[#B00020] font-normal">
      If your boss hits you with{" "}
      <em>“Your soft skills need work…”</em> don’t panic, this is the easiest
      feedback to dismantle.
    </p>

    <p>
      <strong>Why?</strong>
      <br />
      Because soft skills are the <strong>default excuse</strong> when they
      can’t find anything wrong with your actual work. It’s vague,
      subjective, and unprovable, unless{" "}
      <em>you make it provable.</em>
    </p>

    <p>
      So don’t defend. <strong>Demonstrate.</strong>
    </p>
    <p>
  Here’s where your prep matters.
  <br />
  If you’ve anticipated this (and you should), you’ll already have evidence
  that makes the comparison meaningless.
</p>

<p>
  <strong>Leadership:</strong> You led cross-team efforts they didn’t.
  <br />
  <strong>Initiative:</strong> You solved problems no one even owned.
  <br />
  <strong>Team Player:</strong> You upskilled juniors or supported peers
  through crunch moments.
  <br />
  <strong>Execution:</strong> You delivered consistently; they delivered only
  occasionally.
</p>

<p className="text-[#B00020] font-normal">
  If they say you can’t communicate well, use that very meeting as your
  mic-drop moment.
  <br/>
  <p className="text-[#323232]">
  Structure your points clearly. Stay calm. Ask insightful questions.
  <br />
  Let your composure be the proof.
</p>
</p>



<p className="text-[#B00020] font-normal">
  “You don’t have tenure. They’ve been here longer.”
  <p className="text-[#323232] font-normal">
  Great, and dinosaurs were here longer too.
  <br />
  Remember your experience or tenure has nothing to do with promotion. Your
  impact is much greater. The key is knowing your{" "}
  <strong>differentiator in advance</strong>, what sets you apart.
</p>
</p>

<p>
  Highlight it clearly: <em>why you</em>, not <em>why not them</em>.
</p>

<p>
  <em>
    Don’t let the conversation be framed around the tenure but about your
    differentiator. Play to your strengths and don’t get drawn into
    comparisons.
  </em>
</p>

<p className="text-[#B00020] font-normal">
  Budget cuts, hiring freeze, the economy’s everyone’s villain.
  <br />
  <p className="text-[#323232]">
  So what do you do?
  <br />
  You offer initiative in a way that <em>logically justifies your promotion</em>,
  without ever asking for it directly.
</p>
</p>



<p>
  Bring out the “I can be both” move:
  <br />
  <p>
    “I’m already functioning as a tech lead. I can also take on project
    management and cross-team coordination, but that expanded scope works best
    at the next level.”
  </p>
</p>

<p>
  You’re not asking for more work. You’re volunteering to make their life
  easier.
</p>

<p>
  And you’re not begging for a title.
  <br />
  You’re explaining, calmly, rationally, that the{" "}
  <strong>scope requires the position.</strong>
</p>

<p>
  Even limited ownership shows leadership.
  <br />
  Initiative is free, use it.
</p>

<p className="text-[#B00020] font-normal">
  “You’re too good at your job to move you.” Translation:{" "}
  <em>You didn’t delegate.</em>
  <br />
  <p className="text-[#323232]">
  Show how you built systems and trained others, that the machine runs without
  you.
</p>
</p>


<p>
  Tough one. If your boss feels threatened, play the game with strategy. Frame
  your growth as <em>their success.</em>
</p>

<p>
    “It reflects well on you that your team’s being recognized.”
  <br />
  Feed the ego. Protect your peace. Win the long game.
</p>

<p>
  Hurt his ego a little bit that his peer is doing a better job of getting
  people promoted.
</p>

<p>
  When the bar keeps moving, stop chasing it. Do your research on the next
  level, anchor your <em>ask</em> in facts.
</p>

<p>
    “The next role expects X, Y, Z, I’ve demonstrated all three.”
</p>

<p>
  Be crystal clear about two things:
</p>

<ol className="list-decimal list-inside space-y-1 pl-5">
  <li>
    <strong>What you’ll do at the next level.</strong>
  </li>
  <li>
    <strong>
      What you’ve already done that proves you can handle it.
    </strong>
  </li>
</ol>

<p>
  When you show exactly what’s expected and exactly where you stand, the
  conversation changes from{" "}
  <em>“You’re not ready”</em> to{" "}
  <strong>“Oh… this is already happening.”</strong>
</p>

<p>
  Preparation turns perception into persuasion.
</p>

<p>
  Every “no” tells you which lever needs tuning, visibility, perception,
  alignment, or influence. Your job isn’t just to ask for the promotion. It’s
  to ask like someone who’s already earned it.
</p>

  </div>
);

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default HowToPitchVideo;
